import styled, { css, keyframes } from 'styled-components';
import colours from 'theme/atoms/colours';
import * as Text from 'theme/components/Text.style';
import { EventStatus } from 'events/_types';
import { OPEN_COLOUR, RESOLVED_COLOUR } from 'events/styles/DaySummary.style';
import { spin } from 'shiftNotes/styles/shared.style';

export const NOTE_COUNT_PULSE_DURATION_MS = 3000;
export const NOTE_COUNT_PULSE_MIDPOINT_MS = NOTE_COUNT_PULSE_DURATION_MS / 2;

const noteCountPulseScale = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const noteCountPulseColor = keyframes`
  0%, 100% { color: ${colours.neutral[500]}; }
  50% { color: ${colours.neutral[900]}; }
`;

export const UNSEEN_UPDATE_FADE_MS = 10000;

const unseenUpdateFade = keyframes`
  0% { opacity: 0; transform: scale(0.4); }
  3% { opacity: 1; transform: scale(1); }
  75% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1); }
`;

interface TagColors {
  bg: string;
  border: string;
  text: string;
}

const iconWrapper = css`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 16px;
  justify-content: center;
  width: 16px;

  svg {
    height: 16px;
    stroke-width: 2;
    width: 16px;
  }
`;

export const Card = styled.div<{
  $status: EventStatus;
  $selected?: boolean;
  $isPrivate?: boolean;
}>`
  border-radius: 30px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  width: 100%;

  ${({ $status, $selected, $isPrivate }) => {
    if ($selected) {
      return css`
        background-color: ${colours.neutral[200]};
        box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.05);
      `;
    }
    if ($status === 'invisible' || $isPrivate) {
      return css`
        background-color: transparent;
        border: 2px dashed ${colours.neutral[300]};
      `;
    }
    return css`
      background-color: ${colours.shade[0]};
      box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.03),
        0 20px 80px -15px rgba(0, 29, 37, 0.1);
    `;
  }}
`;

export const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 5px;
  width: 100%;
`;

const STATUS_ICON_COLORS: Record<EventStatus, string> = {
  open: OPEN_COLOUR,
  resolved: RESOLVED_COLOUR,
  auto_resolved: RESOLVED_COLOUR,
  invisible: colours.neutral[500],
};

export const StatusIcon = styled.span<{
  $status: EventStatus;
  $isPrivate?: boolean;
}>`
  ${iconWrapper}
  color: ${({ $status, $isPrivate }) =>
    $isPrivate ? colours.neutral[500] : STATUS_ICON_COLORS[$status]};
`;

export const Authors = styled(Text.P1Bold)`
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderMeta = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 5px;
`;

export const NoteCount = styled.span<{ $isPulsing?: boolean }>`
  align-items: center;
  display: inline-flex;
  gap: 1px;
  transform-origin: center;

  ${({ $isPulsing }) =>
    $isPulsing &&
    css`
      animation: ${noteCountPulseScale} ${NOTE_COUNT_PULSE_DURATION_MS}ms
        ease-in-out;

      & > * {
        animation: ${noteCountPulseColor} ${NOTE_COUNT_PULSE_DURATION_MS}ms
          ease-in-out;
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;

        & > * {
          animation: none;
        }
      }
    `}
`;

export const NoteCountIcon = styled.span`
  align-items: center;
  color: ${colours.neutral[500]};
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
`;

export const MetaText = styled(Text.P1).attrs({ as: 'span' })`
  color: ${colours.neutral[500]};
  white-space: nowrap;
`;

export const UnseenUpdateDot = styled.span`
  animation: ${unseenUpdateFade} ${UNSEEN_UPDATE_FADE_MS}ms ease-in-out forwards;
  background-color: ${colours.primary[500]};
  border-radius: 50%;
  display: block;
  flex-shrink: 0;
  height: 7px;
  width: 7px;
`;

export const MetaDot = styled.span`
  background-color: ${colours.neutral[500]};
  border-radius: 50%;
  display: block;
  flex-shrink: 0;
  height: 4px;
  width: 4px;
`;

export const Description = styled(Text.P2)`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const TagList = styled.div`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  width: 100%;
`;

export const Tag = styled.div<{ $colors?: TagColors; $selected?: boolean }>`
  align-items: center;
  background-color: ${({ $colors }) => $colors?.bg ?? 'transparent'};
  border: 2px solid
    ${({ $colors, $selected }) =>
      $colors?.border ??
      ($selected ? colours.neutral[300] : colours.neutral[200])};
  border-radius: 25px;
  box-sizing: border-box;
  color: ${({ $colors }) => $colors?.text ?? colours.neutral[700]};
  display: inline-flex;
  flex-shrink: 0;
  gap: 5px;
  padding: 7px 10px 7px 7px;
`;

export const TagIcon = styled.span`
  ${iconWrapper}
`;

export const TagLabel = styled(Text.LabelSemibold).attrs({ as: 'span' })`
  color: inherit;
  white-space: nowrap;
`;

export const MoreChip = styled.div<{ $selected?: boolean }>`
  align-items: center;
  background-color: transparent;
  border: 2px solid
    ${({ $selected }) =>
      $selected ? colours.neutral[300] : colours.neutral[200]};
  border-radius: 25px;
  box-sizing: border-box;
  color: ${colours.neutral[700]};
  display: inline-flex;
  flex-shrink: 0;
  padding: 7px 10px;
`;

export const ProcessingPill = styled.div`
  align-items: center;
  align-self: flex-start;
  background: ${colours.neutral[100]};
  border-radius: 20px;
  box-sizing: border-box;
  color: ${colours.neutral[500]};
  display: inline-flex;
  gap: 8px;
  padding: 10px 15px;
  width: fit-content;
`;

export const ProcessingPillIcon = styled.span`
  align-items: center;
  color: inherit;
  display: flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  transform: rotate(180deg);
  width: 18px;

  svg {
    animation: ${spin} 2.5s linear infinite;
    height: 18px;
    width: 18px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const ProcessingPillLabel = styled(Text.P2Semibold)`
  color: inherit;
`;
